import { AlertTriangle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { cn } from '@/lib/utils';
import {
  BaseStatus,
  createAssignmentStatus,
  createBillStatus,
  createInvoiceStatus,
  createProjectStatus,
  createTeamMemberStatus,
  StatusBadgeProps,
  StatusBadgeSize,
  StatusBadgeType,
} from '@/modules/common';
import { createClientStatus } from '@/modules/common/components/molecules/badge/status-configs/client-status-config';
import { createOverheadStatus } from '@/modules/common/components/molecules/badge/status-configs/overhead-status-config';
import { createTimeEntryStatus } from '@/modules/common/components/molecules/badge/status-configs/time-entry-status-config';
import { createTimeOffStatus } from '@/modules/common/components/molecules/badge/status-configs/time-off-status-config';

/**
 * Generic status badge component that supports different entity types.
 *
 * Usage examples:
 *
 * // With project status (type-safe)
 * <StatusBadge
 *   status={projectStatus}
 *   label="Active Project"
 * />
 *
 * // With team member status (type-safe)
 * <StatusBadge
 *   status={teamMemberStatus}
 *   label="Invited"
 * />
 *
 * // With warning tooltip
 * <StatusBadge
 *   status={projectStatus}
 *   label="Active Project"
 *   warningTooltip="This project will be archived on Jan 15, 2025"
 * />
 *
 * // With info tooltip
 * <StatusBadge
 *   status={projectStatus}
 *   label="Tentative"
 *   infoTooltip="Probability: 75%"
 * />
 */
export function StatusBadge({
  type,
  status,
  label,
  size = StatusBadgeSize.Default,
  warningTooltip,
  infoTooltip,
}: StatusBadgeProps) {
  // Return null if no status provided
  if (!status || !label) {
    return null;
  }

  const config = getBadgeStatus(type, status).getConfig();

  const badgeContent = (
    <Badge
      variant="outline"
      className={cn(
        'whitespace-nowrap rounded-full bg-transparent font-normal',
        size === StatusBadgeSize.Sm ? 'px-1.5 py-0 text-[10px] leading-4' : 'px-2 text-xs',
        config.colorClasses
      )}
    >
      {label}
      {warningTooltip && <AlertTriangle className="ml-1.5 inline h-3.5 w-3.5" />}
    </Badge>
  );

  if (warningTooltip || infoTooltip) {
    return (
      <SimpleTooltip content={<p>{warningTooltip || infoTooltip}</p>}>{badgeContent}</SimpleTooltip>
    );
  }

  return badgeContent;
}

// Factory function to create status instances based on enum values
export const getBadgeStatus = (type: StatusBadgeType, status: string): BaseStatus => {
  switch (type) {
    case StatusBadgeType.Project:
      return createProjectStatus(status);
    case StatusBadgeType.TeamMember:
      return createTeamMemberStatus(status);
    case StatusBadgeType.Client:
      return createClientStatus(status);
    case StatusBadgeType.Invoice:
      return createInvoiceStatus(status);
    case StatusBadgeType.Bill:
      return createBillStatus(status);
    case StatusBadgeType.Assignment:
      return createAssignmentStatus(status);
    case StatusBadgeType.TimeEntry:
      return createTimeEntryStatus(status);
    case StatusBadgeType.TimeOff:
      return createTimeOffStatus(status);
    case StatusBadgeType.Overhead:
      return createOverheadStatus(status);
    default:
      return createProjectStatus(status); // Default fallback
  }
};
